/*
 *  -----------------------------------------------------------------------
 *    <copyright file="admin-study-data-sets.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import { useLazyQuery } from '@apollo/client/react';
import React, { useEffect, useState } from 'react';

import { DatasetExpandableGrid } from './dataset-expandable-grid';
import { EditDataSetDialog } from './edit-data-set-dialog';
import { useLocalState, useBoolean, useTranslation, useAuthorization } from 'hooks';
import { GET_DATASETS, GET_CM_OBSERVATIONS_AND_ACTIONS } from 'queries/central-monitoring';
import { DataSetFields } from 'queries/generated/fragments.generated';
import NotificationService from 'utils/notification';
import Loading from 'views/components/core/loading';
import PageTitle from 'views/components/core/page-title/page-title';
import Error from 'views/components/error/error';

const AdminStudyDataSets = () => {
    const { state } = useLocalState();
    const { t } = useTranslation();
    const user = useAuthorization();
    const canEdit = user.canWriteSystemAdminLevel();

    const showEditDataSet = useBoolean(false);
    const [currentDataSet, setCurrentDataSet] = useState(null);

    const [getDataSets, { data, loading, error }] = useLazyQuery(GET_DATASETS);
    const [loadData, { loading: loadingObservations, error: errorObservations, data: dataObservations }] = useLazyQuery(
        GET_CM_OBSERVATIONS_AND_ACTIONS,
    );

    useEffect(() => {
        if (state && state.selectedStudy) {
            getDataSets({ variables: { studyId: state.selectedStudy.id } });
            loadData({ variables: { studyId: state.selectedStudy.id } });
        }
    }, [state]);

    const actions = (dataSet: DataSetFields) => [
        {
            label: t('uam:editDataset'),
            icon: ['fal', 'edit'],
            onClick: () => {
                setCurrentDataSet(dataSet);
                showEditDataSet.setTrue();
            },
            enabled: canEdit,
        },
    ];

    const handleCloseModal = (status?: string) => {
        if (status === 'updated')
            NotificationService.showNotification(
                t('uam:datasetHasBeenSuccesfullyUpdated', { datasetId: currentDataSet.recordId }),
            );
        if (status === 'error') NotificationService.showNotification(t('errorMessage'));

        showEditDataSet.setFalse();
    };

    if (!state.selectedStudy) return <h3>{t('common:noStudySelected')}</h3>;

    if (loading || loadingObservations) return <Loading />;

    if (error || errorObservations) return <Error error={error || errorObservations} />;

    return (
        <div>
            <EditDataSetDialog
                currentDataSet={currentDataSet}
                open={showEditDataSet.value}
                close={handleCloseModal}
                observations={dataObservations?.centralMonitoringObservations}
            />
            <PageTitle text={t('uam:manageStudyDatasets')} />
            <DatasetExpandableGrid actions={actions} data={data} />
        </div>
    );
};

export default AdminStudyDataSets;
